import type { ComponentProps, Ref } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon, Minus } from "lucide-react";

import { cn } from "../../lib/utils";

export interface CheckboxProps extends ComponentProps<typeof CheckboxPrimitive.Root> {
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A tri-state checkbox built on Radix. `checked` accepts `true`, `false`, or `"indeterminate"`, the
 * last of which renders a dash — the state for a "select all" control when only some rows are
 * selected. Give it an accessible name via `aria-label` when there is no visible `<label>`.
 *
 * @example
 * ```tsx
 * <Checkbox checked={checked} onCheckedChange={setChecked} aria-label="Select row" />
 * <Checkbox checked="indeterminate" aria-label="Select all rows" />
 * ```
 */
export const Checkbox = ({ className, checked, ref, ...props }: CheckboxProps) => (
  <CheckboxPrimitive.Root
    ref={ref}
    data-slot="checkbox"
    checked={checked}
    className={cn(
      "peer size-4 shrink-0 rounded-[4px] border border-input shadow-xs transition-shadow outline-none",
      "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
      "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
      "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      data-slot="checkbox-indicator"
      className="flex items-center justify-center text-current transition-none"
    >
      {checked === "indeterminate" ? <Minus className="size-3.5" /> : <CheckIcon className="size-3.5" />}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);
